import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Upload, Cpu, Mail, ChevronRight, Rocket, Sparkles,
} from 'lucide-react';
import Starfield from '../components/Starfield';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';

// PLACEHOLDER: ESA/Hubble's iconic 2006 M42 (Orion Nebula) mosaic
// (heic0601a), served straight from the ESA Hubble CDN. Public domain via
// NASA/ESA. Swap this for our own stacked hero image (or a rotating gallery
// of user submissions) before launch — hotlinking a foreign CDN in prod is
// fine for a beta but not polite forever.
const HERO_IMG =
  'https://cdn.esahubble.org/archives/images/wallpaper5/heic0601a.jpg';
const HERO_ATTRIBUTION = 'M42 · NASA / ESA / Hubble';

const Home: React.FC = () => {
  const { isAuthenticated, signIn } = useAuth();

  return (
    <div className="relative bg-space-950">
      {/* ================ HERO ================ */}
      <section className="relative min-h-screen flex flex-col">
        {/* The photo. `fixed`-ish look via absolute + object-cover fills the fold
            without paying the layout-thrash cost of an actual fixed background. */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={HERO_IMG}
            alt="The Orion Nebula (M42) — Hubble Space Telescope mosaic"
            className="h-full w-full object-cover object-center scale-105 animate-fade-in"
            loading="eager"
          />
          {/* Vignette + bottom fade so the copy stays legible over any hotspot in
              the photo, and the section blends into the dark body below. */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(5,6,13,0.55)_75%,#05060d_100%)]" />
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-space-950 via-space-950/70 to-transparent" />
        </div>

        {/* Header rides on top of the photo. */}
        <div className="relative z-20">
          <Header />
        </div>

        {/* Copy pinned bottom-left, generous whitespace above. */}
        <div className="relative z-10 mt-auto px-6 md:px-12 pb-16 md:pb-24 max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/40 border border-white/15 text-white/80 text-xs font-medium mb-6 backdrop-blur">
              <Sparkles className="h-3 w-3 text-nebula-300" />
              Cloud stacking for Seestar
            </div>

            <h1
              className="font-display text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[1.02] mb-6"
              style={{ textShadow: '0 4px 40px rgba(0,0,0,0.6)' }}
            >
              A tall stack
              <br />
              <span className="bg-gradient-to-r from-nebula-200 via-aurora-400 to-nebula-300 bg-clip-text text-transparent">
                of stars.
              </span>
            </h1>

            <p
              className="text-lg md:text-xl text-white/85 leading-relaxed mb-10 max-w-2xl"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.7)' }}
            >
              Point us at your Seestar session folder. We stack it on cloud Graviton
              and email you the master frame. No installs, no laptop fans screaming
              at 3 a.m.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              {isAuthenticated ? (
                <Link
                  to="/jobs/new"
                  className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-nebula-500 hover:bg-nebula-400 text-white font-semibold shadow-nebula-lg transition-all"
                >
                  Start a new stack
                  <ChevronRight className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              ) : (
                <button
                  onClick={signIn}
                  className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-nebula-500 hover:bg-nebula-400 text-white font-semibold shadow-nebula-lg transition-all"
                >
                  <Rocket className="h-5 w-5" />
                  Sign in with Google
                </button>
              )}
              <a
                href="#how"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-white/10 hover:bg-white/15 backdrop-blur text-white font-medium border border-white/20 transition-colors"
              >
                How it works
              </a>
            </div>
          </motion.div>
        </div>

        {/* Attribution — small, corner, un-shouty. */}
        <div className="absolute bottom-3 right-4 z-10 text-[10px] uppercase tracking-widest text-white/40 font-mono">
          {HERO_ATTRIBUTION}
        </div>
      </section>

      {/* ================ REST OF PAGE (over starfield) ================ */}
      <div className="relative">
        <Starfield />
        <div className="relative z-10">

          {/* HOW IT WORKS */}
          <section id="how" className="relative px-6 md:px-10 py-24 max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="font-display text-3xl md:text-4xl font-bold mb-3 text-center"
            >
              How it works.
            </motion.h2>
            <p className="text-center text-white/60 mb-16 max-w-xl mx-auto">
              You keep the data. We rent the machine by the minute.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="relative p-7 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur hover:bg-white/[0.05] transition-colors"
                >
                  <div className="absolute -top-3 -left-3 h-8 w-8 rounded-full bg-nebula-500 text-white font-semibold flex items-center justify-center text-sm shadow-nebula">
                    {i + 1}
                  </div>
                  <s.icon className="h-8 w-8 text-nebula-300 mb-4" />
                  <h3 className="font-display text-lg font-semibold mb-2">{s.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{s.body}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* WHY */}
          <section className="relative px-6 md:px-10 py-20 max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-display text-3xl md:text-4xl font-bold mb-5">
                  Made for Seestar.
                </h2>
                <p className="text-white/70 leading-relaxed mb-6">
                  Seestar sessions land on your phone as folders of FITS. No calibration
                  frames, no fiddling &mdash; you just want to register, stack, and
                  stretch. Our default script does exactly that, tuned for the S30 / S50
                  one-shot color sensor. Need drizzle? More RAM? Pick a bigger instance
                  from the wizard.
                </p>
                <ul className="space-y-3 text-sm text-white/70">
                  {BULLETS.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-aurora-400" />
                      {b}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="absolute -inset-8 bg-nebula-500/20 blur-3xl rounded-full" />
                <div className="relative p-6 rounded-2xl bg-space-900/80 border border-white/10 backdrop-blur font-mono text-xs leading-relaxed text-white/80 shadow-nebula">
                  <div className="text-white/40 mb-2"># one-time — install the CLI</div>
                  <div><span className="text-nebula-300">$</span> brew install glennbech/tap/pancakestack</div>

                  <div className="text-white/40 mt-4 mb-2"># sign in with Google (opens your browser)</div>
                  <div><span className="text-nebula-300">$</span> pancakestack login</div>

                  <div className="text-white/40 mt-4 mb-2"># tar &amp; upload one night's Seestar folder</div>
                  <div><span className="text-nebula-300">$</span> tar -cf m81.tar -C ~/Seestar/M81 .</div>
                  <div><span className="text-nebula-300">$</span> pancakestack upload m81 m81.tar</div>

                  <div className="text-white/40 mt-4 mb-2"># stack it on cloud Graviton</div>
                  <div><span className="text-nebula-300">$</span> pancakestack stack m81 --script seestar</div>
                  <div className="mt-3 text-white/50">✓ launched i-0abc… on c7g.4xlarge (spot)</div>
                  <div className="text-aurora-400">✓ email will land when the master is ready</div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="relative px-6 md:px-10 py-24 max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-display text-4xl md:text-5xl font-bold mb-5"
            >
              Clear skies ahead.
            </motion.h2>
            <p className="text-white/60 mb-8">
              Free while in beta. Google sign-in only. No credit card, no BS.
            </p>
            {!isAuthenticated && (
              <button
                onClick={signIn}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-nebula-500 hover:bg-nebula-400 text-white font-semibold shadow-nebula-lg transition-all"
              >
                <Rocket className="h-5 w-5" />
                Sign in and start stacking
              </button>
            )}
          </section>

          <footer className="relative z-10 border-t border-white/5 py-6 text-center text-xs text-white/40">
            Built on AWS &middot; Siril on Graviton spot &middot;{' '}
            <a
              href="https://github.com/glennbech/siril-cloud"
              className="hover:text-white/70 transition-colors"
              target="_blank" rel="noreferrer"
            >
              source
            </a>
          </footer>
        </div>
      </div>
    </div>
  );
};

const STEPS = [
  {
    icon: Upload,
    title: 'Upload your session',
    body: 'Tar your Seestar session folder and drop it in. Multipart upload straight to S3 with a live progress bar — big archives don\'t care.',
  },
  {
    icon: Cpu,
    title: 'We stack on Graviton',
    body: 'A spot instance boots, installs Siril, and runs the script you picked. Track it live from the jobs page.',
  },
  {
    icon: Mail,
    title: 'Master in your inbox',
    body: 'Final FITS + PNG preview pushed back to S3. Download link arrives by email. The instance self-terminates. Storage bill: pennies.',
  },
];

const BULLETS = [
  'Seestar S30 / S50 defaults — filter roundness, rejection, RGB equalize',
  '2× or 3× drizzle for tight framing (Siril 1.4)',
  'Also handles DSLR RAWs (CR2 / CR3 / NEF / ARW) with debayer',
  'Instance size picker: pay for the machine you actually need',
  'No account limits during beta',
];

export default Home;
